import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type AppTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'ttc-theme';
  private themeSubject: BehaviorSubject<AppTheme>;
  public currentTheme$: Observable<AppTheme>;
  private isDarkModeSubject: BehaviorSubject<boolean>;
  public isDarkMode$: Observable<boolean>;

  constructor() {
    const savedTheme = (localStorage.getItem(this.THEME_STORAGE_KEY) as AppTheme) || 'light';
    const initialTheme: AppTheme = savedTheme === 'dark' ? 'dark' : 'light';

    this.themeSubject = new BehaviorSubject<AppTheme>(initialTheme);
    this.currentTheme$ = this.themeSubject.asObservable();

    this.isDarkModeSubject = new BehaviorSubject<boolean>(initialTheme === 'dark');
    this.isDarkMode$ = this.isDarkModeSubject.asObservable();

    // Apply the saved theme immediately to DOM
    this.applyThemeToDOM(initialTheme);
  }

  public get currentTheme(): AppTheme {
    return this.themeSubject.value;
  }

  public get isDark(): boolean {
    return this.themeSubject.value === 'dark';
  }

  public isTransitioning = false;

  public toggleTheme(event?: MouseEvent): void {
    if (this.isTransitioning) {
      return;
    }
    const nextTheme: AppTheme = this.themeSubject.value === 'dark' ? 'light' : 'dark';
    this.setTheme(nextTheme, event);
  }

  public setTheme(theme: AppTheme, event?: MouseEvent): void {
    if (theme === this.themeSubject.value) {
      return;
    }

    if (typeof document === 'undefined') {
      this.themeSubject.next(theme);
      this.isDarkModeSubject.next(theme === 'dark');
      return;
    }

    // Determine animation origin coordinates
    let x = window.innerWidth - 75;
    let y = 35;

    if (event && typeof event.clientX === 'number') {
      x = event.clientX;
      y = event.clientY;
    } else {
      const btn = document.querySelector('.theme-toggle-btn') as HTMLElement;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
    }

    // Trigger pulse effect on toggle button if available
    const btn = (event?.currentTarget as HTMLElement) || (document.querySelector('.theme-toggle-btn') as HTMLElement);
    if (btn) {
      btn.classList.remove('pulse-wave');
      void btn.offsetWidth; // Trigger reflow
      btn.classList.add('pulse-wave');
      setTimeout(() => btn.classList.remove('pulse-wave'), 750);
    }

    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const doc = document as any;
    const isAppearanceTransition =
      typeof doc.startViewTransition === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!isAppearanceTransition) {
      this.fallbackCircularTransition(theme, x, y, endRadius);
      return;
    }

    this.isTransitioning = true;

    try {
      const transition = doc.startViewTransition(() => {
        this.applyThemeToDOM(theme);
        this.themeSubject.next(theme);
        this.isDarkModeSubject.next(theme === 'dark');
        localStorage.setItem(this.THEME_STORAGE_KEY, theme);
      });

      transition.ready
        .then(() => {
          const clipPath = [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`
          ];

          const animation = document.documentElement.animate(
            {
              clipPath: clipPath
            },
            {
              duration: 600,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
              pseudoElement: '::view-transition-new(root)'
            }
          );

          animation.onfinish = () => {
            this.isTransitioning = false;
          };
          animation.oncancel = () => {
            this.isTransitioning = false;
          };
        })
        .catch(() => {
          this.isTransitioning = false;
        });

      if (transition.finished) {
        transition.finished.finally(() => {
          this.isTransitioning = false;
        });
      }
    } catch {
      this.isTransitioning = false;
      this.applyThemeToDOM(theme);
      this.themeSubject.next(theme);
      this.isDarkModeSubject.next(theme === 'dark');
      localStorage.setItem(this.THEME_STORAGE_KEY, theme);
    }
  }

  private fallbackCircularTransition(theme: AppTheme, x: number, y: number, endRadius: number): void {
    this.isTransitioning = true;
    const overlay = document.createElement('div');
    overlay.className = 'theme-fallback-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 99999999;
      clip-path: circle(0px at ${x}px ${y}px);
      background: ${theme === 'dark' ? '#070d1e' : '#f8fafc'};
      transition: clip-path 0.55s cubic-bezier(0.22, 1, 0.36, 1);
    `;
    document.body.appendChild(overlay);

    void overlay.offsetHeight; // force reflow

    overlay.style.clipPath = `circle(${endRadius}px at ${x}px ${y}px)`;

    setTimeout(() => {
      this.applyThemeToDOM(theme);
      this.themeSubject.next(theme);
      this.isDarkModeSubject.next(theme === 'dark');
      localStorage.setItem(this.THEME_STORAGE_KEY, theme);

      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.25s ease';
      setTimeout(() => {
        overlay.remove();
        this.isTransitioning = false;
      }, 250);
    }, 350);
  }

  private applyThemeToDOM(theme: AppTheme): void {
    if (typeof document === 'undefined') {
      return;
    }

    const html = document.documentElement;
    const body = document.body;

    html.setAttribute('data-theme', theme);
    body.setAttribute('data-theme', theme);

    if (theme === 'dark') {
      html.classList.add('dark-theme');
      body.classList.add('dark-theme');
    } else {
      html.classList.remove('dark-theme');
      body.classList.remove('dark-theme');
    }
  }
}
